import React, { useState, useEffect } from 'react';
import PromptSelector from './components/PromptSelector';
import RoastPanel from './components/RoastPanel';
import PreviousRoasts from './components/PreviousRoasts';
import MarkdownRenderer from './components/MarkdownRenderer';
import { generateRoast } from './services/roastService';
import { loadRoasts, saveRoast } from './services/roastStore';

export interface Evaluation {
  id?: number;
  arxiv_paper_id: string;
  paper_pdf_filepath: string;
  evaluation_uuid: string;
  full_evaluation_text: string;
  prompt_type: string;
  paper_title?: string;
  paper_authors?: string;
  paper_url?: string;
  paper_abstract?: string;
  created_at?: string;
  prompt_style?: string;
  prompt_components?: string;
  harshness_level?: number;
  tweet_review?: string;
}

enum Stage {
  INPUT = 'input',
  CONFIGURE = 'configure',
  RESULT = 'result',
}

interface PaperMetadata {
  paperId: string;
  title: string;
}

function newUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'r-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function App() {
  // Stage management
  const [stage, setStage] = useState<Stage>(Stage.INPUT);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Paper data (derived from the uploaded file — no backend metadata lookup)
  const [paperMetadata, setPaperMetadata] = useState<PaperMetadata | null>(null);

  // Evaluation data
  const [evaluation, setEvaluation] = useState<string>('');
  const [tweetReview, setTweetReview] = useState<string>('');
  const [evaluationUuid, setEvaluationUuid] = useState<string>('');
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation | null>(null);
  const [showPanel, setShowPanel] = useState(false);

  // Prompt configuration
  const [promptComponents, setPromptComponents] = useState({
    style: 'meme',
    harshness: 3,
    length: 'standard',
    customUserPrompt: undefined as string | undefined,
  });

  // API configuration (BYOK — the key stays in the browser)
  const [apiKey, setApiKey] = useState(localStorage.getItem('anthropicApiKey') || '');
  const [rememberKey, setRememberKey] = useState(!!localStorage.getItem('anthropicApiKey'));

  // Load previous roasts (from localStorage) on mount
  useEffect(() => {
    setEvaluations(loadRoasts());
  }, []);

  // Persist API key when "remember" is checked
  useEffect(() => {
    if (rememberKey && apiKey) {
      localStorage.setItem('anthropicApiKey', apiKey);
    } else if (!rememberKey) {
      localStorage.removeItem('anthropicApiKey');
    }
  }, [apiKey, rememberKey]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError('');
    } else if (file) {
      setError('Please select a PDF file');
    }
  };

  // Stage 1 -> 2: accept the PDF + key, move to configuration
  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!selectedFile) {
      setError('Please choose a PDF to roast.');
      return;
    }
    if (!apiKey.trim()) {
      setError('An Anthropic API key is required (it stays in your browser).');
      return;
    }
    const title = selectedFile.name.replace(/\.pdf$/i, '');
    setPaperMetadata({ paperId: title, title });
    setStage(Stage.CONFIGURE);
  };

  // Stage 2 -> 3: generate the roast entirely client-side
  const handleGenerateOutput = async () => {
    if (!selectedFile || !paperMetadata) return;
    if (!apiKey.trim()) {
      setError('An Anthropic API key is required.');
      return;
    }

    setLoading(true);
    setGenerating(true);
    setError('');
    setEvaluation('');
    setTweetReview('');
    const uuid = newUuid();
    setEvaluationUuid(uuid);
    setStage(Stage.RESULT);

    try {
      const { evaluation: evalText, tweetReview: tweet } = await generateRoast({
        file: selectedFile,
        apiKey,
        components: promptComponents,
        onText: (soFar) => setEvaluation(soFar),
      });

      setEvaluation(evalText);
      setTweetReview(tweet);

      const record: Evaluation = {
        arxiv_paper_id: paperMetadata.paperId,
        paper_pdf_filepath: selectedFile.name,
        evaluation_uuid: uuid,
        full_evaluation_text: evalText,
        prompt_type: 'component_based',
        paper_title: paperMetadata.title,
        created_at: new Date().toISOString(),
        prompt_style: promptComponents.style,
        prompt_components: JSON.stringify(promptComponents),
        harshness_level: promptComponents.harshness,
        tweet_review: tweet,
      };
      saveRoast(record);
      setEvaluations(loadRoasts());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate the roast.');
      setStage(Stage.CONFIGURE);
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  };

  const reset = () => {
    setStage(Stage.INPUT);
    setPaperMetadata(null);
    setEvaluation('');
    setTweetReview('');
    setEvaluationUuid('');
    setError('');
    setSelectedFile(null);
    setGenerating(false);
  };

  const handleSelectEvaluation = (evaluation: Evaluation) => {
    setSelectedEvaluation(evaluation);
    setShowPanel(true);
  };

  const handleTweetShare = () => {
    // No shared backend, so we tweet the text itself rather than a link that
    // would only resolve in this browser.
    const tweetText = encodeURIComponent(`${tweetReview}\n\nvia YourResearchSucks`);
    window.open(`https://x.com/intent/tweet?text=${tweetText}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-neutral-dark relative overflow-x-hidden font-serif">
      <div className={`transition-transform duration-300 ${showPanel ? '-translate-x-12 md:-translate-x-20' : ''}`}>
        <div className="max-w-6xl mx-auto p-4 md:p-8">
          <div className="bg-neutral-darker rounded-lg shadow-lg p-6 md:p-8 border border-neutral-gray">
            <h1 className="text-critique-primary mb-2 text-center text-4xl md:text-6xl font-display font-black tracking-tight">
              YourResearchSucks.
            </h1>
            <p className="text-neutral-lighter text-center mb-8 text-lg md:text-xl italic">
              Most research is bad and useless. Is yours?
            </p>

            {/* Stage 1: PDF upload + API key */}
            {stage === Stage.INPUT && (
              <form onSubmit={handleContinue}>
                <div className="space-y-4 mb-4">
                  <div className="border-2 border-dashed border-neutral-light rounded-lg p-8 text-center hover:border-critique-primary transition-all">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="pdf-upload"
                      disabled={loading}
                    />
                    <label htmlFor="pdf-upload" className="cursor-pointer">
                      {selectedFile ? (
                        <div>
                          <p className="text-white mb-2">Selected file:</p>
                          <p className="text-critique-primary font-semibold">{selectedFile.name}</p>
                          <p className="text-neutral-lighter text-sm mt-2">Click to choose a different file</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-white mb-2">Click to select a PDF file</p>
                          <p className="text-neutral-lighter text-sm">or drag and drop your paper</p>
                        </div>
                      )}
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-critique-primary text-white font-semibold rounded transition-all hover:bg-critique-secondary disabled:bg-neutral-light disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={loading || !selectedFile || !apiKey.trim()}
                  >
                    Continue
                  </button>
                </div>

                {/* API Key Input (required — BYOK) */}
                <div className="mb-4 p-4 bg-neutral-gray rounded border border-neutral-light">
                  <div className="flex items-center gap-4 mb-2">
                    <input
                      type="password"
                      className="flex-1 px-3 py-2 bg-neutral-darker text-white rounded border border-neutral-light focus:outline-none focus:border-critique-primary text-sm placeholder:text-neutral-lighter"
                      placeholder="Anthropic API Key (required)"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                    />
                    <label className="flex items-center text-neutral-lighter text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={rememberKey}
                        onChange={(e) => setRememberKey(e.target.checked)}
                        className="mr-2"
                      />
                      Remember
                    </label>
                  </div>
                  <p className="text-xs text-neutral-lighter">
                    Your key stays in your browser and is sent only to Anthropic — never to us. Get one at{' '}
                    <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" className="text-critique-primary hover:underline">
                      console.anthropic.com
                    </a>
                    .
                  </p>
                </div>

                {error && <div className="text-red-500 text-center mb-4">{error}</div>}
              </form>
            )}

            {/* Stage 2: Configuration */}
            {stage === Stage.CONFIGURE && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-center text-white">Configure Your Roast</h2>

                <div className="bg-green-900/20 border border-green-700 rounded p-3 text-center">
                  <p className="text-green-400">
                    ✓ {paperMetadata?.title || 'Paper'} ready — choose your roast style below.
                  </p>
                </div>

                <PromptSelector
                  components={promptComponents}
                  onChange={setPromptComponents}
                  isComponentMode={true}
                />

                <button
                  onClick={handleGenerateOutput}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-critique-primary text-white font-semibold rounded transition-all hover:bg-critique-secondary disabled:bg-neutral-light disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Generating your roast...' : 'Generate Roast'}
                </button>

                <button
                  onClick={reset}
                  className="w-full px-4 py-2 bg-neutral-gray text-white rounded border border-neutral-light hover:bg-neutral-light hover:text-white transition-all"
                >
                  Upload a different paper
                </button>

                {error && <div className="text-red-500 text-center">{error}</div>}
              </div>
            )}

            {/* Stage 3: Results */}
            {stage === Stage.RESULT && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-white">
                    {generating ? 'Roasting Your Paper…' : 'Your Paper Has Been Roasted'}
                  </h2>
                  <button
                    onClick={reset}
                    className="px-4 py-2 bg-neutral-gray text-white rounded border border-neutral-light hover:bg-neutral-light hover:text-white transition-all"
                  >
                    Roast Another Paper
                  </button>
                </div>

                {/* Paper Info & Tweet Review */}
                {paperMetadata && (
                  <div className="bg-neutral-gray rounded-lg p-6 border border-neutral-light space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{paperMetadata.title}</h3>
                    </div>

                    {tweetReview && (
                      <div className="border-t border-neutral-light pt-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-critique-primary mb-2">Tweet-Length Roast:</h4>
                            <p className="text-white text-lg font-medium">{tweetReview}</p>
                          </div>
                          <button
                            onClick={handleTweetShare}
                            className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            Tweet This
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Full Review (streams live) */}
                <div className="bg-neutral-gray rounded-lg p-6 border border-neutral-light max-h-[600px] overflow-y-auto">
                  <h4 className="text-sm font-semibold text-critique-primary mb-4">Your Roast:</h4>
                  {evaluation ? (
                    <MarkdownRenderer content={evaluation} />
                  ) : (
                    <p className="text-neutral-lighter italic">
                      Claude is reading your paper and sharpening its knives…
                    </p>
                  )}
                  {generating && evaluation && (
                    <p className="text-neutral-lighter text-sm mt-4 animate-pulse">▌ generating…</p>
                  )}
                </div>

                {!generating && (
                  <div className="flex gap-4">
                    <button
                      onClick={() => setStage(Stage.CONFIGURE)}
                      className="flex-1 px-4 py-2 bg-neutral-gray text-white rounded border border-neutral-light hover:bg-neutral-light hover:text-white transition-all"
                    >
                      Try Different Style
                    </button>
                    <button
                      onClick={reset}
                      className="flex-1 px-4 py-2 bg-critique-primary text-white rounded hover:bg-critique-secondary transition-all"
                    >
                      Roast New Paper
                    </button>
                  </div>
                )}

                {error && <div className="text-red-500 text-center">{error}</div>}
              </div>
            )}
          </div>

          {/* Previous Evaluations (from localStorage) */}
          <PreviousRoasts evaluations={evaluations} onSelectEvaluation={handleSelectEvaluation} />
        </div>
      </div>

      {/* Selected Evaluation Panel */}
      {selectedEvaluation && (
        <RoastPanel
          evaluation={selectedEvaluation}
          isOpen={showPanel}
          onClose={() => setShowPanel(false)}
        />
      )}
    </div>
  );
}

export default App;
